/**
 * adminWithdrawalStore - Withdrawal requests slice of the admin store (Zustand).
 * Exposes the withdrawal endpoints (`/api/withdrawals`).
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

export const withdrawalInitialState = {
  withdrawals: [],
  withdrawalsLoading: false,
  withdrawalsError: null,
  withdrawalsTotal: 0,
  withdrawalsPages: 1,
  withdrawalDetail: null,
  withdrawalDetailLoading: false,
  withdrawalDetailError: null,
  withdrawalMutationLoading: false,
  withdrawalMutationError: null,
};

function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items: payload.items || payload.withdrawals || payload.requests || [],
    total: payload.total ?? payload.totalItems ?? payload.count ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

export function createWithdrawalActions(set, get) {
  return {
    fetchWithdrawals: async (params = {}) => {
      set({ withdrawalsLoading: true, withdrawalsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, value]) => value !== undefined && value !== "")
        ).toString();
        const response = await fetch(`${API_URL}/api/withdrawals${query ? `?${query}` : ""}`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch withdrawals");
          set({ withdrawalsLoading: false, withdrawalsError: error });
          return { success: false, error };
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          withdrawalsLoading: false,
          withdrawals: items,
          withdrawalsTotal: total,
          withdrawalsPages: totalPages,
        });
        return { success: true, withdrawals: items, total, pages: totalPages };
      } catch (err) {
        set({ withdrawalsLoading: false, withdrawalsError: err.message });
        return { success: false, error: err.message };
      }
    },

    fetchWithdrawalById: async (id) => {
      set({ withdrawalDetailLoading: true, withdrawalDetailError: null });
      try {
        const response = await fetch(`${API_URL}/api/withdrawals/${id}`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to fetch withdrawal");
          set({ withdrawalDetailLoading: false, withdrawalDetailError: error });
          return { success: false, error };
        }
        const withdrawal = data?.data || data?.withdrawal || data;
        set({ withdrawalDetailLoading: false, withdrawalDetail: withdrawal });
        return { success: true, withdrawal };
      } catch (err) {
        set({ withdrawalDetailLoading: false, withdrawalDetailError: err.message });
        return { success: false, error: err.message };
      }
    },

    approveWithdrawal: async (id) => {
      set({ withdrawalMutationLoading: true, withdrawalMutationError: null });
      try {
        const response = await fetch(`${API_URL}/api/withdrawals/${id}/approve`, {
          method: "PATCH",
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to approve withdrawal");
          set({ withdrawalMutationLoading: false, withdrawalMutationError: error });
          return { success: false, error };
        }
        set({ withdrawalMutationLoading: false, successMessage: "Withdrawal approved" });
        return { success: true };
      } catch (err) {
        set({ withdrawalMutationLoading: false, withdrawalMutationError: err.message });
        return { success: false, error: err.message };
      }
    },

    rejectWithdrawal: async (id, reason) => {
      set({ withdrawalMutationLoading: true, withdrawalMutationError: null });
      try {
        const response = await fetch(`${API_URL}/api/withdrawals/${id}/reject`, {
          method: "PATCH",
          headers: authHeaders(resolveToken(get)),
          body: JSON.stringify({ rejectionReason: reason }),
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          const error = toDisplayString(data.error || data.message, "Failed to reject withdrawal");
          set({ withdrawalMutationLoading: false, withdrawalMutationError: error });
          return { success: false, error };
        }
        set({ withdrawalMutationLoading: false, successMessage: "Withdrawal rejected" });
        return { success: true };
      } catch (err) {
        set({ withdrawalMutationLoading: false, withdrawalMutationError: err.message });
        return { success: false, error: err.message };
      }
    },
  };
}

export const withdrawalActions = createWithdrawalActions;
export default createWithdrawalActions;
